import { useState, useEffect } from "react";
import type { WorkGroup } from "./WorksManagement";
import type { WorksPlacement } from "./WorksPlacementManager";
import { WorksPlacementCard } from "./WorksPlacementCard";
import { api, IMAGE_PROCESSOR_URL } from "../../lib/api";
import { getPublicUrl } from "../../lib/utils/storage";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

interface WorksPlacementListProps {
  works: WorkGroup[];
  placements: WorksPlacement[];
  onDelete: (id: string) => Promise<void>;
  onUpdate: () => Promise<void>;
}

interface ImageUrlCache {
  [key: string]: string;
}

export function WorksPlacementList({
  works,
  placements,
  onDelete,
  onUpdate,
}: WorksPlacementListProps) {
  const [imageUrls, setImageUrls] = useState<ImageUrlCache>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIsUpdating] = useState(false);

  useEffect(() => {
    const fetchImageUrls = async () => {
      const newImageUrls: ImageUrlCache = {};

      for (const placement of placements) {
        if (
          placement.illustration.storage_path &&
          !imageUrls[placement.illustration.storage_path]
        ) {
          try {
            const url = await getPublicUrl(
              "thumbnails",
              placement.illustration.storage_path
            );
            newImageUrls[placement.illustration.storage_path] = url;
          } catch (error) {
            console.error("画像URLの取得に失敗しました:", error);
          }
        }
      }

      setImageUrls((prev) => ({ ...prev, ...newImageUrls }));
    };

    fetchImageUrls();
  }, [placements]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const workId = parseInt(result.source.droppableId.replace("work-", ""));

    const workPlacements = placements
      .filter((p) => p.works_id === workId)
      .sort((a, b) => a.display_order - b.display_order);

    const [movedItem] = workPlacements.splice(sourceIndex, 1);
    workPlacements.splice(destinationIndex, 0, movedItem);

    setIsUpdating(true);
    try {
      for (let i = 0; i < workPlacements.length; i++) {
        await api.updateWorksPlacement(workPlacements[i].id, { display_order: i });
      }
      await onUpdate();
    } catch (error) {
      console.error("並び替えの更新に失敗しました:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-center text-gray-700">
        配置済み画像一覧
      </h2>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-4">
          {works.map((work) => {
            const workPlacements = placements.filter(
              (p) => p.works_id === work.id
            );
            if (workPlacements.length === 0) return null;

            const maxOrder = Math.max(
              ...workPlacements.map((p) => p.display_order),
              0
            );

            return (
              <div key={work.id} className="border rounded-lg p-4">
                <div className="flex items-center gap-4 mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {work.name}
                  </h3>
                  {work.icon_path && (
                    <img
                      src={`${IMAGE_PROCESSOR_URL}/?path=${encodeURIComponent(work.icon_path)}&width=128`}
                      alt={work.name}
                      className="w-8 h-8 object-contain"
                    />
                  )}
                </div>
                <Droppable droppableId={`work-${work.id}`}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="grid grid-cols-1 gap-4"
                    >
                      {workPlacements
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((placement, index) => (
                          <Draggable
                            key={placement.id}
                            draggableId={`placement-${placement.id}`}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <WorksPlacementCard
                                  placement={placement}
                                  imageUrl={
                                    imageUrls[
                                      placement.illustration.storage_path
                                    ]
                                  }
                                  onDelete={onDelete}
                                  onUpdate={onUpdate}
                                  maxOrder={maxOrder}
                                  workPlacements={workPlacements}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
